import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import buildHomeScopeKey from '@UI/Pages/Home/HomeHeader/HomeFilters/Helpers/buildHomeScopeKey';
import buildScopeOptions, {
	type ScopeOption,
	type ScopeOptionGroup,
} from '@UI/Pages/Home/HomeHeader/HomeFilters/Helpers/buildScopeOptions';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import { setHomeScope } from '@UI/Pages/Home/Signals/homeScopeSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { Dropdown } from '@verclam/ui-kit';

type Props = {
	/** Shows the visible "Périmètre" label (narrow viewport); the accessible name is always there. */
	showLabel: boolean;
};

const pillStyle = css`
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	padding: 0.6875rem 1rem;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.borderStrong};
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.2;
	color: ${theme.colors.ink};
`;

const singleFacilityStyle = css`
	${pillStyle}
	margin: 0;
`;

const fieldStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;

	.ui-kit-field {
		margin: 0;
	}

	.ui-kit-field-label {
		font-family: ${theme.fonts.body};
		font-size: ${theme.fontSizes.small};
		font-weight: ${theme.fontWeights.normal};
		color: ${theme.colors.inkMuted};
	}

	.ui-kit-dropdown-control-wrapper {
		--ui-kit-field-input-min-height: 0;
		--ui-kit-field-input-padding: 0.6875rem 1rem;
		--ui-kit-field-input-radius: ${theme.radii.pill};
		--ui-kit-field-input-border-width: 1.5px;
		--ui-kit-field-input-border: ${theme.colors.borderStrong};
		--ui-kit-field-input-border-hover: ${theme.colors.accent};
		--ui-kit-field-input-border-focus: ${theme.colors.accent};
		--ui-kit-field-input-bg: ${theme.colors.surface};
		--ui-kit-field-input-text: ${theme.colors.ink};
		--ui-kit-field-input-font-family: ${theme.fonts.body};
		--ui-kit-field-input-font-size: 0.8125rem;
		--ui-kit-field-input-shadow-focus: none;
		--ui-kit-dropdown-icon-text: ${theme.colors.ink};
		font-weight: ${theme.fontWeights.medium};
		line-height: 1.2;
	}

	.ui-kit-dropdown-control-wrapper:has(:focus-visible) {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

const panelStyle = {
	'--ui-kit-dropdown-panel-bg': theme.colors.surface,
	'--ui-kit-dropdown-panel-radius': theme.radii.medium,
	'--ui-kit-dropdown-panel-shadow': 'none',
	'--ui-kit-dropdown-panel-font-family': theme.fonts.body,
	'--ui-kit-dropdown-panel-font-size': '0.8125rem',
	'--ui-kit-dropdown-list-padding': '0.375rem',
	'--ui-kit-dropdown-item-padding': '0.625rem 0.875rem',
	'--ui-kit-dropdown-item-radius': '12px',
	'--ui-kit-dropdown-item-group-text': theme.colors.ink,
	'--ui-kit-dropdown-item-group-padding': '0.625rem 0.875rem',
	'--ui-kit-surface-hover': theme.colors.surfaceMuted,
	'--ui-kit-selected-bg': theme.colors.accentSurface,
	'--ui-kit-selected-bg-strong': theme.colors.accentSurface,
	'--ui-kit-selected-text': theme.colors.accentDark,
	minWidth: '18.75rem',
	maxWidth: '84vw',
	border: `1.5px solid ${theme.colors.borderStrong}`,
	fontWeight: theme.fontWeights.medium,
} as React.CSSProperties;

const PASS_THROUGH = { panel: { style: panelStyle } };

const renderGroupLabel = (group: unknown): React.ReactNode => {
	const { label } = group as ScopeOptionGroup;

	return label === '' ? null : label;
};

const facilityOptionStyle = css`
	padding-left: 0.875rem;
`;

const renderOption = (option: unknown): React.ReactNode => {
	const { label, scope } = option as ScopeOption;

	return scope.kind === 'facility' ? <span css={facilityOptionStyle}>{label}</span> : label;
};

/**
 * Périmètre filter, built from the caller's organizations only. With a single agency there is
 * nothing to choose: an inert pill; with none, nothing at all.
 */
const ScopeFilter: React.FC<Props> = ({ showLabel }) => {
	const { organizations } = useCurrentOrganization();
	const { scope } = useHomeIndicatorScope();
	const options = React.useMemo(() => buildScopeOptions(organizations), [organizations]);

	const handleChange = React.useCallback(
		(key: string | null) => {
			const selected = options.groups
				.flatMap((group) => group.items)
				.find((option) => option.key === key);

			if (selected) {
				setHomeScope(selected.scope);
			}
		},
		[options],
	);

	if (options.onlyFacilityName !== null) {
		return <p css={singleFacilityStyle}>{`Agence ${options.onlyFacilityName}`}</p>;
	}

	if (options.groups.length === 0) {
		return null;
	}

	return (
		<div css={fieldStyle}>
			<Dropdown<string>
				name="homeScope"
				label="Périmètre"
				isLabelHidden={!showLabel}
				options={options.groups}
				optionGroupLabel="label"
				optionGroupTemplate={renderGroupLabel}
				optionLabel="label"
				optionValue="key"
				itemTemplate={renderOption}
				value={buildHomeScopeKey(scope)}
				onChange={handleChange}
				pt={PASS_THROUGH}
			/>
		</div>
	);
};

export default ScopeFilter;
